import { KeyboardEvent, MouseEvent, useCallback } from 'react'
import clsx from 'clsx'
import { Link, useLocation } from 'wouter'
import { useQuery } from '@apollo/client'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'
import { HugeiconsIcon } from '@hugeicons/react'
import { useLingui } from '@lingui/react/macro'

import {
  CheckmarkSquare02Icon,
  PauseIcon,
  PlayIcon,
  SquareIcon
} from '@hugeicons/core-solid-rounded'

import * as Table from '@/components/ui/Table'
import { helpers } from '@/features/goodies'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { Permission, useCheckPermission } from '@/features/auth'

interface RowProps {
  id: number
  isDisabled: boolean
  isSelected: boolean
  hideCheckbox: boolean
  onSelect: () => void
  onToggleActive: (active: boolean) => void
}

export default function PromotionRow({
  id,
  isDisabled,
  isSelected,
  hideCheckbox,
  onSelect,
  onToggleActive
}: RowProps) {
  const { t } = useLingui()
  const goodieTypeToLabel = helpers.useTypeToLabel()
  const canPausePromotion = useCheckPermission([Permission.PROMOTION_UPDATE])

  const { data: promoData } = useQuery(AdminAPI.promotion.GET, {
    variables: { id },
    fetchPolicy: 'cache-only'
  })

  const promotion = getFragment(
    SharedAPI.promotion.PROMOTION_FRAGMENT,
    promoData?.getPromotion
  )

  const { data: goodieData } = useQuery(AdminAPI.goodie.GET, {
    variables: { id: promotion?.goodieId ?? -1 },
    skip: !promotion
  })

  const goodie = getFragment(
    SharedAPI.goodie.GOODIE_FRAGMENT,
    goodieData?.getGoodie
  )

  const [, navigate] = useLocation()

  const onEdit = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      event.stopPropagation()

      navigate(`/promotion/${promotion?.token}`)
    },
    [promotion, navigate]
  )

  const onSelectProxy = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onSelect()
    },
    [onSelect]
  )

  if (!promotion) return null

  const promotionName = promotion.name

  const toggleTitle = promotion?.isActive
    ? t`Deactivate Promotion`
    : t`Activate Promotion`

  const toggleMessage = promotion?.isActive
    ? t`Are you sure you want to deactivate the Promotion "${promotionName}", guests will not see the promotion anymore?`
    : t`Are you sure you want to activate the Promotion "${promotionName}", guests will see the promotion?`

  return (
    <Table.Row
      key={promotion.token}
      className={clsx(
        'cursor-pointer',
        isSelected && 'is-active',
        isDisabled && 'pointer-events-none opacity-50'
      )}
      onClick={onEdit}
      onEnter={onEdit}
    >
      {!hideCheckbox && (
        <Table.Cell>
          <div className="flex flex-row items-center justify-center">
            <button onClick={onSelectProxy} tabIndex={0}>
              {isSelected && (
                <HugeiconsIcon
                  icon={CheckmarkSquare02Icon}
                  className="text-primary"
                />
              )}
              {!isSelected && (
                <HugeiconsIcon icon={SquareIcon} className="text-primary" />
              )}
            </button>
          </div>
        </Table.Cell>
      )}
      <Table.Cell>{promotion.name}</Table.Cell>
      <Table.Cell>
        <Link
          to={`/goodie/${goodie?.token}`}
          // Doing it this way, so that we still can do shift click to open it in a new tab
          // but just open it as modal when clicking normally
          onClickCapture={event => {
            event.stopPropagation()

            if (event.metaKey || event.ctrlKey) return

            event.preventDefault()
            navigate(`/goodie/${goodie?.token}`)
          }}
          className="text-info-400 underline"
        >
          {goodie?.title}
        </Link>
      </Table.Cell>
      <Table.Cell>{goodie && goodieTypeToLabel[goodie?.type]}</Table.Cell>
      <Table.Cell>
        {canPausePromotion && (
          <div className="flex flex-row gap-5">
            <ConfirmDialog
              title={toggleTitle}
              message={toggleMessage}
              onConfirm={() => onToggleActive(!promotion.isActive)}
              aria-label={t`Promote`}
            >
              {promotion.isActive && (
                <HugeiconsIcon icon={PauseIcon} className="size-5" />
              )}
              {!promotion.isActive && (
                <HugeiconsIcon icon={PlayIcon} className="size-5" />
              )}
            </ConfirmDialog>
          </div>
        )}
      </Table.Cell>
    </Table.Row>
  )
}
